// HR-004 follow-up — Biometric device + employee mapping API client.
import apiClient from '@/shared/api/client'

export interface BiometricDevice {
  id: string
  plant_id: string
  serial_number: string
  device_name: string
  device_model: string | null
  location: string | null
  ip_address: string | null
  default_direction: 'IN' | 'OUT' | 'UNKNOWN'
  is_active: boolean
  last_seen_at: string | null
  force_resend_until: string | null
  pending_command: string | null
  pending_command_set_at: string | null
  remarks: string | null
}

export interface BiometricDeviceCreate {
  plant_id: string
  serial_number: string
  device_name: string
  device_model?: string | null
  location?: string | null
  ip_address?: string | null
  default_direction?: 'IN' | 'OUT' | 'UNKNOWN'
  remarks?: string | null
}

export interface BiometricDevicePatch {
  device_name?: string
  device_model?: string | null
  location?: string | null
  ip_address?: string | null
  default_direction?: 'IN' | 'OUT' | 'UNKNOWN'
  is_active?: boolean
  remarks?: string | null
}

export interface EmployeeMapping {
  id: string
  emp_code: string
  emp_name: string
  department_id: string | null
  biometric_id: string | null
  is_active: boolean
}

export interface BulkMapRow {
  emp_code: string
  biometric_id: string
}

export interface BulkMapResult {
  ok: boolean
  error?: string
  updated: number
  skipped: number
  errors: number
  rows: Array<{
    emp_code: string
    biometric_id: string
    status: 'UPDATED' | 'UNCHANGED' | 'UNMAPPED' | 'ERROR'
    reason?: string
  }>
}

export const biometricApi = {
  // Devices
  listDevices: async (params: {
    plant_id?: string; is_active?: boolean
  } = {}) =>
    (await apiClient.get<BiometricDevice[]>('/biometric/devices', { params })).data,
  createDevice: async (body: BiometricDeviceCreate) =>
    (await apiClient.post<BiometricDevice>('/biometric/devices', body)).data,
  patchDevice: async (id: string, body: BiometricDevicePatch) =>
    (await apiClient.patch<BiometricDevice>(`/biometric/devices/${id}`, body)).data,
  // Force the device to re-push its full flash buffer on next handshake.
  // Returns { device_id, force_resend_until (ISO), note }.
  forceResend: async (id: string) =>
    (await apiClient.post<{
      device_id: string
      serial_number: string
      device_name: string
      force_resend_until: string
      note: string
    }>(`/biometric/devices/${id}/force-resend`)).data,
  // Queue a REBOOT command via the eSSL ADMS command queue. The device's
  // next /getrequest poll (~15-30 sec) returns the command; it reboots
  // and re-handshakes, which consumes force_resend_until. This is the
  // reliable way to trigger Force Re-upload in normal operation —
  // force_resend_until alone only fires on natural handshake (boot /
  // network reconnect).
  rebootDevice: async (id: string, also_force_resend = true) =>
    (await apiClient.post<{
      device_id: string
      serial_number: string
      device_name: string
      pending_command: string
      pending_command_set_at: string
      force_resend_until: string | null
      note: string
    }>(`/biometric/devices/${id}/reboot`, null,
       { params: { also_force_resend } })).data,
  cancelCommand: async (id: string) =>
    (await apiClient.post<{ device_id: string; cleared: boolean }>(
       `/biometric/devices/${id}/cancel-command`)).data,

  // Employee mappings
  listMappings: async (params: {
    plant_id?: string; mapped_only?: boolean
  } = {}) =>
    (await apiClient.get<EmployeeMapping[]>('/biometric/employees-mapping',
      { params })).data,
  mapEmployee: async (employee_id: string, biometric_id: string) =>
    (await apiClient.post<{ employee_id: string; biometric_id: string }>(
      '/biometric/map-employee',
      { employee_id, biometric_id }
    )).data,
  bulkMap: async (rows: BulkMapRow[], plant_id?: string) =>
    (await apiClient.post<BulkMapResult>('/biometric/map-employees-bulk',
      { rows, plant_id, clear_unlisted: false })).data,
}
